import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { SelectProps, Option } from 'components/forms/SelectInput/SelectInput';
import styles from './SelectWithTagsNew.module.scss';

export interface SelectWithTagsProps extends Omit<SelectProps, 'onChange'> {
  label: string;
  value: string[];
  onChange: (options: string[]) => void;
}

const SelectWithTagsNew: React.FC<SelectWithTagsProps> = (props) => {
  const {
    name,
    value,
    label,
    options,
    className,
    placeholder,
    hasError,
    disabled,
    onChange = () => {},
    ...otherProps
  } = props;
  const [showOptionList, setShowOptionList] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [optionsList, setOptionsList] = useState(options);

  const handleSelectClick = useCallback((e) => {
    setShowOptionList((prev) => !prev);
  }, []);

  const handleOptionClick = useCallback(
    (option) => {
      setSelectedOptions((prev) => {
        const newOptions = [...prev, option];
        const newValues = newOptions.map((option) => option.value);
        onChange(newValues);
        return newOptions;
      });
      setOptionsList((prev) => prev.filter((item) => item.value !== option.value));
      setShowOptionList(false);
    },
    [onChange]
  );

  const handleClickOutside = useCallback((e) => {
    if (
      !e.target.classList.contains(styles.option) &&
      !e.target.classList.contains(styles.selectbox)
    ) {
      setShowOptionList(false);
    }
  }, []);

  const handleRemoveTag = useCallback(
    (e, option) => {
      e.stopPropagation();
      setSelectedOptions((prev) => {
        const filteredOptions = prev.filter((item) => item.value !== option.value);
        const filteredValues = filteredOptions.map((option) => option.value);
        onChange(filteredValues);
        return filteredOptions;
      });
      setOptionsList([...optionsList, option]);
    },
    [onChange, optionsList]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const rootClassName = classnames(styles.root, className, {
    [styles.filled]: value.length && !showOptionList,
    [styles.error]: hasError,
    [styles.disabled]: disabled,
    [styles.listOpen]: showOptionList,
  });

  const iconClassName = classnames(styles.icon, 'fas', 'fa-sort');

  const tags = selectedOptions.map((option: Option) => (
    <li className={styles.tag} key={option.value}>
      <span className={styles.tagLabel}>{option.label}</span>
      <button
        className={styles.tagButton}
        onClick={(e) => handleRemoveTag(e, option)}
        type='button'
      >
        <span className='fa fa-times'></span>
      </button>
    </li>
  ));

  const optionsItems = optionsList?.map((option: Option) => (
    <li className={styles.listItem} key={option.value}>
      <button className={styles.option} onClick={() => handleOptionClick(option)} type='button'>
        {option.label}
      </button>
    </li>
  ));

  return (
    <div className={rootClassName}>
      <div
        className={styles.selectbox}
        onClick={handleSelectClick}
        onKeyDown={() => {}}
        tabIndex={0}
        role='button'
        {...otherProps}
      >
        <span className={styles.placeholder}>{placeholder}</span>
        <span className={iconClassName}></span>
      </div>
      <ul className={styles.tags}>{tags}</ul>
      {showOptionList && <ul className={styles.list}>{optionsItems}</ul>}
    </div>
  );
};

export default SelectWithTagsNew;
